// @oasis-permission-public: open to any signed-in Oasis user
import * as Dialog from "@radix-ui/react-dialog";
import * as RadioGroup from "@radix-ui/react-radio-group";
import { createFileRoute, useRouter, useNavigate } from "@tanstack/react-router";
import { decodeJwt } from "jose";
import { useEffect, useState } from "react";
import { toast } from "sonner";

import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { useAuthStore } from "~/modules/auth/auth-store";
import { loginCredentials } from "~/modules/auth/credentials";
import { changeUserRoleMutation } from "~/modules/auth/network";
import { AVAILABLE_ROLES, getRoleDisplayName } from "~/modules/auth/roles";

export const Route = createFileRoute("/_dash/profile")({
  component: RouteComponent,
});

function getCurrentRole(): string | null {
  try {
    const credentials = loginCredentials.get();
    if (!credentials) {
      return null;
    }

    const decoded = decodeJwt(credentials.accessToken);
    return (decoded as { role: string })?.role || null;
  } catch (error) {
    console.error("Failed to decode access token:", error);
    return null;
  }
}

function RouteComponent() {
  const [user, logout] = useAuthStore((s) => [s.user, s.logout]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const currentRole = getCurrentRole();
  const canChangeRole = user?.permissions.includes("profile-role:write") ?? false;
  const navigate = useNavigate();

  const onLogout = async () => {
    await logout("logout_clicked");
    navigate({ to: "/" });
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-bold">Profile</h1>
      </div>

      {user && (
        <div className="bg-card border rounded-lg p-4">
          <div className="space-y-2">
            <div>
              <span className="font-medium">Name:</span> {user.name}
            </div>
            <div>
              <span className="font-medium">Email:</span> {user.email}
            </div>
            <div>
              <span className="font-medium">User ID:</span> {user.userId}
            </div>
            {currentRole && (
              <div>
                <span className="font-medium">Current Role:</span>{" "}
                <Badge variant="secondary">{getRoleDisplayName(currentRole)}</Badge>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="flex gap-2">
        <Button onClick={() => setIsDialogOpen(true)} disabled={!canChangeRole}>
          Change Role
        </Button>
        <Button onClick={onLogout} variant="destructive">
          Sign out
        </Button>
      </div>

      <ChangeRoleDialog isOpen={isDialogOpen} onClose={() => setIsDialogOpen(false)} />
    </div>
  );
}
function ChangeRoleDialog({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const router = useRouter();
  const [saveLoginCredentials, refreshProfile] = useAuthStore((s) => [
    s.saveLoginCredentials,
    s.refreshProfile,
  ]);
  const currentRole = getCurrentRole();
  const [selectedRole, setSelectedRole] = useState<string>("");

  // Set current role as selected when dialog opens
  useEffect(() => {
    if (isOpen && currentRole) {
      setSelectedRole(currentRole);
    }
  }, [isOpen, currentRole]);

  const { mutate: changeRole, isPending } = changeUserRoleMutation.useMutation({
    onSuccess: async (newCredentials) => {
      saveLoginCredentials(newCredentials);
      await refreshProfile();
      toast.success("Role changed successfully");
      await router.invalidate();
      onClose();
    },
    onError: (error) => {
      toast.error("Failed to change role");
      console.error("Role change error:", error);
    },
  });

  const handleRoleChange = () => {
    if (!selectedRole) {
      toast.error("Please select a role");
      return;
    }

    const credentials = loginCredentials.get();
    if (!credentials) {
      toast.error("No credentials found");
      return;
    }

    changeRole({
      params: {
        role: selectedRole,
        refreshToken: credentials.refreshToken,
      },
    });
  };

  return (
    <Dialog.Root open={isOpen} onOpenChange={onClose}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/50 z-50" />
        <Dialog.Content className="fixed left-1/2 top-1/2 max-w-lg w-full max-h-[85vh] -translate-x-1/2 -translate-y-1/2 bg-white dark:bg-gray-900 rounded-lg border shadow-lg z-50 p-6">
          <Dialog.Title className="text-lg font-semibold mb-2">Change Role</Dialog.Title>
          <Dialog.Description className="text-sm text-muted-foreground mb-6">
            Select a new role to change to.
          </Dialog.Description>

          <RadioGroup.Root
            value={selectedRole}
            onValueChange={setSelectedRole}
            className="grid grid-cols-2 gap-3 mb-6"
          >
            {AVAILABLE_ROLES.map((role) => (
              <RadioGroup.Item
                key={role.id}
                value={role.id}
                className="group p-3 border rounded-lg cursor-pointer transition-colors data-[state=checked]:border-blue-500 data-[state=checked]:bg-blue-50 dark:data-[state=checked]:bg-blue-900/20 border-gray-200 hover:border-gray-300 dark:border-gray-700 dark:hover:border-gray-600 outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
              >
                <div className="font-medium text-sm">{role.name}</div>
              </RadioGroup.Item>
            ))}
          </RadioGroup.Root>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onClose} disabled={isPending}>
              Cancel
            </Button>
            <Button onClick={handleRoleChange} disabled={isPending || !selectedRole}>
              {isPending ? "Changing..." : "Change Role"}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
